import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  COLLECTION_ID,
  SelectionType,
  Space,
  Track,
} from "@highnote/server/src/core/entities";
import { PERMISSION } from "@highnote/server/src/core/shared-util";
import { FullyConfiguredDialogV2 } from "App/common/DialogV2";
import { useHighnote } from "App/common/useHighnote";
import { useToast } from "App/common/useToast";
import {
  DEFAULT_COLUMNS,
  ENTITY_TYPE,
  EntityRowConfig,
} from "App/components/EntityTable/config";
import { EmptyTable } from "App/components/EntityTable/EmptyTable";
import { sortRowByAlphabetical } from "App/components/util";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useAuth } from "../Auth";
import { SearchEntityList } from "../SearchEntityList";
import { sanitizeSelections } from "../useEntitiesSelection";
import { SearchContextProvider } from "./useSearchContext";

import styles from "./useMoveSelectionsToTarget.module.scss";

type MoveSelectionsToTargetContextType = {
  openMoveSelectionsToTarget: (props: {
    selectedEntities: Record<string, SelectionType>;
    onSuccess?: () => void;
  }) => void;
};

const MoveSelectionsToTargetContext =
  createContext<MoveSelectionsToTargetContextType>({
    openMoveSelectionsToTarget: () => {},
  });

export const MoveSelectionsToTargetProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { addErrorMessage } = useToast();
  const [isOpen, setOpen] = useState(false);
  const [entitiesToMove, setEntitiesToMove] = useState<
    Record<string, SelectionType>
  >({});
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [currentRoot, setCurrentRoot] = useState<Space>();

  const onSuccessRef = useRef<() => void>();
  const { moveSelectionToEntity } = useHighnote();
  const { getGlobalSpace } = useGlobalSpaces();
  const { isAllowed } = useAuth();

  const spaceSelections = useMemo(
    () =>
      Object.values(entitiesToMove).filter(
        (entity) => entity.entityType === COLLECTION_ID.SPACE,
      ),
    [entitiesToMove],
  );

  const hasSpaceSelections = Object.values(spaceSelections).length > 0;
  const trackSelections = useMemo(
    () =>
      Object.values(entitiesToMove).filter(
        (entity) => entity.entityType === COLLECTION_ID.TRACK,
      ),
    [entitiesToMove],
  );
  const hasTrackSelections = Object.values(trackSelections).length > 0;

  // Memoize the set of parent entity IDs for efficient lookup in transformRows
  const parentEntityIds = useMemo(
    () =>
      new Set(
        Object.values(entitiesToMove).map(
          (selected) => selected.parentEntity.entityId,
        ),
      ),
    [entitiesToMove],
  );

  /**
   * Disables rows that are not valid move targets (e.g., parent entities or those without permission)
   * by removing their onClick handler. Only rows with an onClick handler are considered clickable.
   * The actual move action is handled by the onSelect prop passed to SearchEntityList.
   */
  const transformRows = (rows: EntityRowConfig[]) => {
    return rows.map((row) => {
      let isParentOfSelected = false;
      if (row.type === ENTITY_TYPE.SPACE) {
        isParentOfSelected = parentEntityIds.has((row.entity as Space).id);
      }
      if (row.type === ENTITY_TYPE.TRACK) {
        isParentOfSelected = parentEntityIds.has((row.entity as Track).id);
      }

      const isClickable =
        !isParentOfSelected &&
        (row.type === ENTITY_TYPE.TRACK
          ? isAllowed(PERMISSION.TO_ADD_TO_TRACK_IN_SPACE, {
              track: row.entity as Track,
              space: getGlobalSpace((row.entity as Track).spaceId),
            })
          : true);

      const { onClick, ...rowData } = row;

      if (isClickable) return { ...rowData, onClick };

      return rowData;
    });
  };

  const resetStatus = () => {
    setIsMoving(false);
    setEntitiesToMove({});
    setCurrentRoot(undefined);
  };

  useEffect(() => {
    if (!isOpen) {
      resetStatus();
    }
  }, [isOpen]);

  const filter = (row: EntityRowConfig) => {
    // filter out a row if it's in the list of entities to move
    if ((row.entity as Entity).id in entitiesToMove) {
      return false;
    }
    return hasSpaceSelections
      ? // if you are moving a space(s), you can only move selections
        // to a space, so we hide anything but spaces
        row.type === ENTITY_TYPE.SPACE
      : // but if you did not select any spaces, you can move selections
        // to either a space or a track, so we hide anything but spaces & tracks
        row.type === ENTITY_TYPE.SPACE || row.type === ENTITY_TYPE.TRACK;
  };

  const canAddToSpace = (destinationSpace: Space) => {
    return isAllowed(PERMISSION.TO_ADD_TO_SPACE, {
      space: destinationSpace,
    });
  };

  const onSelect = async (row: EntityRowConfig) => {
    if (
      row.type === ENTITY_TYPE.SPACE &&
      row.entity &&
      !canAddToSpace(row.entity as Space)
    ) {
      addErrorMessage("You do not have permission to add to this space.");
      return;
    }

    setIsMoving(true);
    try {
      await moveSelectionToEntity({
        targetEntity: row.entity
          ? {
              entityId: (row.entity as Space | Track).id,
              entityType:
                row.type === ENTITY_TYPE.SPACE
                  ? COLLECTION_ID.SPACE
                  : COLLECTION_ID.TRACK,
            }
          : null,
        selectedEntities: sanitizeSelections(entitiesToMove),
      });
      onSuccessRef.current?.();
      setOpen(false);
      // eslint-disable-next-line no-empty
    } catch (e) {
    } finally {
      setIsMoving(false);
    }
  };

  const value = useMemo(
    () => ({
      openMoveSelectionsToTarget: ({
        selectedEntities,
        onSuccess,
      }: {
        selectedEntities: Record<string, SelectionType>;
        onSuccess: () => void;
      }) => {
        resetStatus();
        setEntitiesToMove(selectedEntities);
        setOpen(true);
        onSuccessRef.current = onSuccess;
      },
    }),
    [],
  );

  return (
    <MoveSelectionsToTargetContext.Provider value={value}>
      <FullyConfiguredDialogV2
        open={isOpen}
        onClose={() => {
          setOpen(false);
        }}
        header={{
          title: `Move to...`,
        }}
        contentProps={{
          removePadding: true,
        }}
        PaperProps={{
          className: styles["highnote-move-selection-paper"],
        }}
      >
        {/* TODO: If needed we can raise the search context provider to the top level. 
        For now, we keep it here because the search context is only used in the move selections to target dialog. */}
        <SearchContextProvider>
          <SearchEntityList
            libraryRoot={{
              disableLibraryRoot: hasTrackSelections,
              onClick: () => {
                onSelect({
                  entity: undefined,
                  type: ENTITY_TYPE.SPACE,
                  id: "",
                });
              },
            }}
            parentSpace={currentRoot}
            tableBaseProps={{
              columns: [DEFAULT_COLUMNS.ICON, DEFAULT_COLUMNS.PREVIEW],
              disabled: isMoving,
              entireRowClickable: true,
              filter,
              sort: sortRowByAlphabetical,
              transformRows,
              onSelect, // just the move handler
              EmptyComponent: () => (
                <EmptyTable action="Create or add content" />
              ),
            }}
          />
        </SearchContextProvider>
      </FullyConfiguredDialogV2>
      {children}
    </MoveSelectionsToTargetContext.Provider>
  );
};

export const useMoveSelectionsToTarget = () => {
  const context = useContext(MoveSelectionsToTargetContext);
  if (!context) {
    throw new Error(
      "useMoveSelectionsToTarget must be used within a MoveSelectionsToTargetProvider",
    );
  }
  return context;
};
